import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useProduct, useProductsByCategory, updateProduct, createProduct, ProductDetail } from '../../api/products/products-api';
import { resolveStorageUrl } from '../../api/storageClient';
import { useAuth } from '../../api/auth/AuthContext';
import './ReviewProduct.css';

// ── Schema validation ────────────────────────────────────────────────────────

type FieldType = 'string' | 'number' | 'boolean' | 'string[]';
type Schema = Record<string, FieldType>;

const CATEGORY_SCHEMAS: Record<string, Schema> = {
  'agricultural': {
    $type: 'string', model: 'string', type: 'string',
    capacities: 'string[]', applications: 'string[]',
    description: 'string', features: 'string[]',
  },
  'belt-scales': {
    $type: 'string', model: 'string', capacities: 'string[]',
    dimensions: 'string', speed: 'string', memory: 'string',
    conveyorWidths: 'string[]', heightRange: 'string',
    description: 'string', features: 'string[]',
  },
  'bench-scales': {
    $type: 'string', model: 'string', capacities: 'string[]',
    technology: 'string', display: 'string',
    divisions: 'number', maxDivisions: 'number',
    connectivity: 'string[]', protection: 'string[]',
    material: 'string', certifications: 'string[]',
    description: 'string', features: 'string[]',
  },
  'checkweighing-scales': {
    $type: 'string', model: 'string', baseType: 'string',
    capacities: 'string[]', platformSizes: 'string[]',
    protection: 'string[]', power: 'string[]',
    certifications: 'string[]', description: 'string', features: 'string[]',
  },
  'counting-scales': {
    $type: 'string', model: 'string', capacities: 'string[]',
    connectivity: 'string[]', technology: 'string',
    resolution: 'string', description: 'string', features: 'string[]',
  },
  'dynamometers': {
    $type: 'string', model: 'string', type: 'string',
    capacities: 'string[]', applications: 'string[]',
    protection: 'string', units: 'string[]',
    remoteControl: 'string', power: 'string',
    description: 'string', features: 'string[]',
  },
  'floor-scales': {
    $type: 'string', model: 'string', capacities: 'string[]',
    dimensions: 'string', rampOptions: 'string[]',
    use: 'string', industry: 'string', portable: 'boolean',
    height: 'string', material: 'string',
    description: 'string', features: 'string[]',
  },
  'force-equipment': {
    $type: 'string', model: 'string', type: 'string', subType: 'string',
    capacities: 'string[]', applications: 'string[]',
    measurementTime: 'string', description: 'string', features: 'string[]',
  },
  'forklift-scales': {
    $type: 'string', capacity: 'string', platformSizes: 'string[]',
    compatibleIndicators: 'string[]', description: 'string', features: 'string[]',
  },
  'load-cells': {
    $type: 'string', model: 'string', brand: 'string',
    capacityRange: 'string', materials: 'string[]',
    suspension: 'string', protection: 'string',
    cableLength: 'string', includedComponents: 'string[]',
    note: 'string', applications: 'string[]',
    description: 'string', features: 'string[]',
  },
  'monorail-scales': {
    $type: 'string', capacity: 'string', railLength: 'string',
    junctionBox: 'string', cableLength: 'string',
    description: 'string', features: 'string[]',
  },
  'printers': {
    $type: 'string', model: 'string', type: 'string', subType: 'string',
    width: 'string', applications: 'string[]',
    connectivity: 'string[]', paperTypes: 'string[]',
    description: 'string', features: 'string[]',
  },
  'railway-scales': {
    $type: 'string', model: 'string', type: 'string',
    capacities: 'string[]', platformOptions: 'string[]',
    weighingModes: 'string[]', technicalSpecs: 'string[]',
    included: 'string[]', description: 'string', features: 'string[]',
  },
  'truck-scales': {
    $type: 'string', model: 'string', construction: 'string',
    lengthRange: 'string', mainBeams: 'string', crossBeams: 'string',
    loadCells: 'string', width: 'string', height: 'string',
    included: 'string[]', optionalIndicators: 'string[]',
    optionalAddons: 'string[]', description: 'string', features: 'string[]',
  },
  'weight-indicators': {
    $type: 'string', model: 'string', presentations: 'string[]',
    applications: 'string[]', connectivity: 'string[]',
    description: 'string', features: 'string[]',
  },
};

interface ValidationIssue {
  kind: 'error' | 'warning';
  message: string;
}

function validateSpecs(specsText: string, category: string): ValidationIssue[] {
  let parsed: Record<string, any>;
  try {
    parsed = JSON.parse(specsText);
  } catch {
    return [{ kind: 'error', message: 'El JSON no es válido.' }];
  }

  const schema = CATEGORY_SCHEMAS[category];
  if (!schema) return [{ kind: 'warning', message: `No hay contrato definido para la categoría "${category}".` }];

  const issues: ValidationIssue[] = [];

  for (const key of Object.keys(parsed)) {
    if (!(key in schema)) {
      issues.push({ kind: 'error', message: `Campo desconocido: "${key}"` });
    } else {
      const expected = schema[key];
      const value = parsed[key];
      if (value === null || value === undefined) continue;
      if (expected === 'string[]') {
        if (!Array.isArray(value)) {
          issues.push({ kind: 'error', message: `"${key}" debe ser un arreglo de cadenas.` });
        }
      } else if (expected === 'number') {
        if (typeof value !== 'number') {
          issues.push({ kind: 'error', message: `"${key}" debe ser un número.` });
        }
      } else if (expected === 'boolean') {
        if (typeof value !== 'boolean') {
          issues.push({ kind: 'error', message: `"${key}" debe ser verdadero/falso (boolean).` });
        }
      } else if (expected === 'string') {
        if (typeof value !== 'string') {
          issues.push({ kind: 'error', message: `"${key}" debe ser una cadena de texto.` });
        }
      }
    }
  }

  const typeLabel: Record<FieldType, string> = {
    'string':   'texto',
    'number':   'número',
    'boolean':  'verdadero/falso',
    'string[]': 'lista',
  };

  for (const key of Object.keys(schema)) {
    if (key === '$type') continue;
    if (!(key in parsed)) {
      const label = typeLabel[schema[key]];
      issues.push({ kind: 'warning', message: `Campo faltante: "${key}" (${label})` });
    }
  }

  return issues;
}

function toPascalCase(str: string): string {
  return str
    .replace(/[^a-zA-Z0-9\s]/g, ' ')            // special chars → space
    .trim()
    .replace(/(?:^|\s+)(.)/g, (_, c) => c.toUpperCase()); // capitalise every word
}

function extractType(specsText: string): string {
  try {
    const specs = JSON.parse(specsText);
    return specs?.$type ? toPascalCase(specs.$type) : '';
  } catch {
    return '';
  }
}

function generateId(specsText: string, name: string): string {
  const typePart = extractType(specsText);
  const namePart = name.replace(/\s/g, '_');
  if (typePart && namePart) return `${typePart}.${namePart}`;
  if (typePart)             return typePart;
  return namePart;
}

function emptySchemaFrom(obj: Record<string, any>): Record<string, any> {
  const result: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (key === '$type')                                  result[key] = value;
    else if (Array.isArray(value))                        result[key] = [];
    else if (value !== null && typeof value === 'object') result[key] = emptySchemaFrom(value);
    else if (typeof value === 'number')                   result[key] = 0;
    else if (typeof value === 'boolean')                  result[key] = false;
    else                                                  result[key] = '';
  }
  return result;
}

const EMPTY_PRODUCT = (category: string): ProductDetail => ({
  productId: '',
  category,
  name: '',
  image: null,
  specs: {},
  sortOrder: 1,
  isActive: true,
  reviewed: false,
  reviewedBy: null,
  endOfLife: false,
});

const ReviewProduct: React.FC = () => {
  const { account } = useAuth();
  const { productId, category } = useParams<{ productId?: string; category?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as any)?.from as string | undefined;

  const isCreate = !productId && !!category;
  const { product, loading, error } = useProduct(isCreate ? '' : (productId ?? ''));
  const { products: categoryProducts } = useProductsByCategory(isCreate ? category! : '');

  const [form, setForm] = useState<ProductDetail | null>(
    isCreate ? { ...EMPTY_PRODUCT(category!), reviewedBy: null } : null
  );
  const [specsText, setSpecsText] = useState(isCreate ? '{}' : '');
  const [schemaReady, setSchemaReady] = useState(false);
  const [specsError, setSpecsError] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [validationIssues, setValidationIssues] = useState<ValidationIssue[] | null>(null);

  useEffect(() => {
    if (account?.name) {
      setForm(prev => prev ? { ...prev, reviewedBy: account.name ?? null } : prev);
    }
  }, [account?.name]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isCreate && product) {
      setForm({ ...product, reviewedBy: account?.name ?? null });
      setSpecsText(JSON.stringify(product.specs ?? {}, null, 2));
    }
  }, [product, isCreate]);

  useEffect(() => {
    if (isCreate && !schemaReady && categoryProducts.length > 0) {
      const template = emptySchemaFrom(categoryProducts[0].specs ?? {});
      setSpecsText(JSON.stringify(template, null, 2));
      setSchemaReady(true);
    }
  }, [isCreate, categoryProducts, schemaReady]);

  useEffect(() => {
    if (!isCreate || !form) return;
    const newId = generateId(specsText, form.name ?? '');
    setForm(prev => prev ? { ...prev, productId: newId } : prev);
  }, [isCreate, specsText, form?.name]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isCreate && loading) return <div className="review-state">Cargando producto...</div>;
  if (!isCreate && error)   return <div className="review-state review-state--error">Error: {error}</div>;
  if (!form)                return null;

  const handleChange = (field: keyof ProductDetail, value: any) => {
    setForm(prev => prev ? { ...prev, [field]: value } : prev);
  };

  const handleSpecsChange = (text: string) => {
    setSpecsText(text);
    setSpecsError(null);
    setValidationIssues(null);
    try {
      JSON.parse(text);
    } catch {
      setSpecsError('JSON inválido');
    }
  };

  const handleValidate = () => {
    const effectiveCategory = form?.category ?? category ?? '';
    setValidationIssues(validateSpecs(specsText, effectiveCategory));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (specsError) return;

    let parsedSpecs: Record<string, any>;
    try {
      parsedSpecs = JSON.parse(specsText);
    } catch {
      setSpecsError('JSON inválido');
      return;
    }

    if (isCreate && !form.productId) {
      setSaveError('El ID no pudo generarse. Asegúrese de ingresar un nombre y que el JSON contenga $type.');
      return;
    }

    if (isCreate && !imageFile) {
      setSaveError('Debe seleccionar una imagen para el nuevo producto.');
      return;
    }

    setSaving(true);
    setSaveError(null);
    const payload = { ...form, specs: parsedSpecs };
    const goBack = () => from ? navigate(from, { state: { editMode: true } }) : navigate(-1);
    const callbacks = {
      onSuccess: () => { setSaving(false); goBack(); },
      onBadRequest: (msg: string) => { setSaveError(msg); setSaving(false); },
      onError: (msg: string) => { setSaveError(msg); setSaving(false); },
    };
    if (isCreate) {
      await createProduct(payload, callbacks, imageFile!);
    } else {
      await updateProduct(payload, callbacks, imageFile ?? undefined);
    }
  };

  return (
    <div className="review-product">
      <div className="review-product-header">
        <button className="btn-back" onClick={() => from ? navigate(from, { state: { editMode: true } }) : navigate(-1)}>← Volver</button>
        <div>
          <h1>{isCreate ? 'Nuevo Producto' : 'Revisar Producto'}</h1>
          {!isCreate && <span className="review-product-id">{form.productId}</span>}
        </div>
      </div>

      <form className="review-form" onSubmit={handleSubmit}>

        <div className="form-group">
          <label>ID de Producto *</label>
          <input
            type="text"
            value={form.productId}
            disabled
            readOnly
          />
        </div>

        <div className="form-group">
          <label>Nombre *</label>
          <input
            type="text"
            value={form.name}
            required
            onChange={e => handleChange('name', e.target.value)}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Imagen {isCreate && <span className="label-hint">(requerida)</span>}</label>
            {!isCreate && form.image && !imageFile && (
              <div className="review-current-image">
                <img
                  src={resolveStorageUrl(`/data/products/images/${form.image}`)}
                  alt={form.name ?? ''}
                  className="review-image-preview"
                />
                <a
                  href={resolveStorageUrl(`/data/products/images/${form.image}`)}
                  download={form.image}
                  className="review-download-link"
                >
                  Descargar imagen actual
                </a>
              </div>
            )}
            {imageFile && (
              <div className="review-current-image">
                <img
                  src={URL.createObjectURL(imageFile)}
                  alt="Vista previa"
                  className="review-image-preview"
                />
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={e => setImageFile(e.target.files?.[0] ?? null)}
            />
            {!isCreate && (
              <span className="label-hint">Opcional — dejar vacío para mantener la imagen actual</span>
            )}
          </div>
          <div className="form-group form-group--narrow">
            <label>Orden</label>
            <input
              type="number"
              value={form.sortOrder}
              min={0}
              onChange={e => handleChange('sortOrder', Number(e.target.value))}
            />
          </div>
        </div>

        <div className="form-group">
          <div className="specs-label-row">
            <label>
              Especificaciones
            </label>
            <button
              type="button"
              className="btn-validate"
              disabled={!!specsError}
              onClick={handleValidate}
            >
              Validar
            </button>
          </div>
          <textarea
            className={specsError ? 'has-error' : ''}
            value={specsText}
            rows={16}
            spellCheck={false}
            onChange={e => handleSpecsChange(e.target.value)}
          />
          {specsError && <span className="field-error">{specsError}</span>}
          {validationIssues !== null && (
            <div className={`specs-validation ${validationIssues.length === 0 ? 'specs-validation--ok' : validationIssues.some(i => i.kind === 'error') ? 'specs-validation--error' : 'specs-validation--warn'}`}>
              {validationIssues.length === 0 ? (
                <span className="specs-validation-ok">✓ JSON conforme al contrato de datos</span>
              ) : (
                <ul className="specs-validation-list">
                  {validationIssues.map((issue, i) => (
                    <li key={i} className={`specs-validation-item specs-validation-item--${issue.kind}`}>
                      {issue.kind === 'error' ? '✗' : '⚠'} {issue.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <div className="form-row form-row--checks">
          <label className="check-label">
            <input
              type="checkbox"
              checked={!!form.isActive}
              onChange={e => handleChange('isActive', e.target.checked)}
            />
            Activo
          </label>
          <label className="check-label">
            <input
              type="checkbox"
              checked={!!form.endOfLife}
              onChange={e => handleChange('endOfLife', e.target.checked)}
            />
            Fin de vida (End of Life)
          </label>
        </div>

        <div className="form-group">
          <label>Revisado por</label>
          <input
            type="text"
            value={form.reviewedBy ?? ''}
            disabled 
            readOnly
          />
        </div>

        {saveError && <div className="save-error">{saveError}</div>}

        <div className="form-actions">
          <button type="button" className="btn-cancel" onClick={() => from ? navigate(from, { state: { editMode: true } }) : navigate(-1)}>
            Cancelar
          </button>
          <button type="submit" className="btn-save" disabled={saving || !!specsError}>
            {saving ? 'Guardando...' : isCreate ? 'Crear producto' : 'Guardar cambios'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ReviewProduct;
